import { useEffect, useState, type ReactNode } from "react";
import HeaderV2 from "./HeaderV2";
import AuthModal from "@/components/AuthModal";
import AdGeneratorFooterLink from "@/components/AdGeneratorFooterLink";
import AdToolsFooterLinks from "@/components/AdToolsFooterLinks";
import { CreativeLandingShellContext } from "./creativeLandingShellContext";
import {
  GlobalNav,
  useDirectorMobileViewport,
} from "@/components/director/DirectorShell";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { ensureLandingFonts } from "@/lib/landingFonts";
import type { AuthModalTrigger } from "@/lib/dataLayer";
import "@/locales/registerNewLandingI18n";
import "@/locales/registerLandingV2I18n";
import "@/pages/landing-v2.css";
import "@/pages/landing-v2-app.css";
import "@/pages/creative-tools.css";
import "@/pages/free-tool-landing.css";
import "@/pages/library-landing.css";

export default function CreativeLandingShell({
  children,
  phoneTabs = false,
}: {
  children: ReactNode;
  /** Signed-in app pages (the library): below desktop width, show the app's
   *  bottom tab bar too, as the workspace does. */
  phoneTabs?: boolean;
}) {
  const [authOpen, setAuthOpen] = useState(false);
  const [authTrigger, setAuthTrigger] = useState<AuthModalTrigger>("unknown");
  const openAuth = (trigger: AuthModalTrigger) => {
    setAuthTrigger(trigger);
    setAuthOpen(true);
  };
  const { navigate, withLanguagePath } = useLanguageRouter();
  const isMobile = useDirectorMobileViewport();
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    try {
      return localStorage.getItem("solmi-theme") === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });
  useEffect(() => {
    ensureLandingFonts();
  }, []);
  // Desktop gets the app rail, signed in or not: the same sidebar as
  // /projects and the workspace. Phones keep the top nav.
  const showRail = !isMobile;
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("solmi-theme", next);
    } catch {}
  };
  // The rail is only ever added beside .rail-main, so the page never
  // remounts when the window crosses the desktop width. Below it, GlobalNav
  // renders the fixed bottom tab bar instead, so the page clears it.
  const showTabBar = phoneTabs && isMobile;
  return (
    <div
      className="slv2 creative-landing"
      data-theme={theme}
      style={
        showTabBar
          ? { paddingBottom: "calc(58px + env(safe-area-inset-bottom))" }
          : undefined
      }
    >
      <div className="rail-row">
        {(showRail || showTabBar) && <GlobalNav />}
        <div className="rail-main">
          <HeaderV2
            rail={showRail}
            onLogin={() => openAuth("header_sign_in")}
            onJoin={() => openAuth("header_join")}
            onThemeToggle={toggleTheme}
          />
          <CreativeLandingShellContext.Provider value={true}>
            {children}
          </CreativeLandingShellContext.Provider>
          <footer>
            <a
              className="mark"
              href={withLanguagePath("/")}
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              Solmi<i>.</i>
            </a>
            <nav aria-label="Footer">
              {[
                ["All tools", "/creative-tools"],
                ["Pricing", "/subscription"],
                ["Help", "/help"],
              ].map(([label, href]) => (
                <a
                  key={href}
                  href={withLanguagePath(href)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(href);
                  }}
                >
                  {label}
                </a>
              ))}
              <AdGeneratorFooterLink />
            </nav>
            <AdToolsFooterLinks />
          </footer>
        </div>
      </div>
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        trigger={authTrigger}
      />
    </div>
  );
}
